import { DatePickerInput } from '@mantine/dates';
import classNames from 'classnames/bind';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'locales';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { isDefined, isString } from 'remeda';
import { validationMessage } from 'shared/utils/validation';

import styleIdentifiers from './DateSelector.module.scss';

const styles = classNames.bind(styleIdentifiers);

type DateSelectorProps = {
  disabled?: boolean;
  endName?: string;
  endPlaceholder?: string;
  handleChange: Function;
  handleEndChange?: Function;
  label?: string;
  minDate?: Dayjs;
  name: string;
  noMinDate?: boolean;
  placeholder?: string;
  required?: boolean;
};

export const DateSelector = ({
  disabled,
  endName,
  endPlaceholder,
  handleChange,
  handleEndChange,
  label,
  minDate, // What
  noMinDate, // the fuck
  name,
  placeholder,
  required,
}: DateSelectorProps) => {
  const { t, currentLang } = useTranslation();

  const {
    watch,
    register,
    unregister,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    register(name, { required });
    if (endName) register(endName);

    return () => {
      unregister(name);
      if (endName) unregister(endName);
    };
  }, []);

  const date = watch(name);
  const endDate = endName && watch(endName);

  const error = get(errors, name);
  const errorMessage = isDefined(error)
    ? t(
        validationMessage[
          (isString(error.type) ? error.type : 'invalid') as keyof typeof validationMessage
        ],
      )
    : null;

  const onSelectStartDate = (value: string | null) => {
    handleChange(value);
  };

  const onSelectEndDate = (value: string | null) => {
    if (handleEndChange) handleEndChange(value);
  };

  return (
    <>
      {label && <div className={styles('label-input')}>{label}</div>}
      <DatePickerInput
        locale={currentLang}
        minDate={minDate?.toDate()}
        onChange={onSelectStartDate}
        value={date}
        placeholder={placeholder ?? label}
        disabled={disabled}
        error={errorMessage}
      />
      {endName && (
        <DatePickerInput
          locale={currentLang}
          minDate={noMinDate ? undefined : date.toDate()}
          onChange={onSelectEndDate}
          value={endDate}
          placeholder={endPlaceholder}
          disabled={disabled}
        />
      )}
    </>
  );
};
